import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Button, IconButton, Checkbox, FormControlLabel } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppLayout from "../../components/AppLayout";
import { FornecedorService } from "../../service/fornecedor_service";

// Importar os componentes das abas
import AbaEmpresa from "./abas/AbaEmpresa";
import AbaProdutos from "./abas/AbaProdutos";

function FornecedoresCad() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const [abaAtiva, setAbaAtiva] = useState("empresa");
	const [loading, setLoading] = useState(false);
	const [salvando, setSalvando] = useState(false);
	// Quando o fornecedor está vinculado a uma propriedade do Sisagri Campo, o
	// distribuidor pode optar por gerar a solicitação para que essa propriedade
	// cadastre a unidade distribuidora como cliente no campo.
	const [solicitarClienteCampo, setSolicitarClienteCampo] = useState(false);

	// Estados para cada aba
	const [dadosEmpresa, setDadosEmpresa] = useState(
		location.state?.dadosPreenchidos || {}
	);
	const [produtos, setProdutos] = useState([]);

	// Referência para função de adicionar documento da AbaEmpresa
	const [addDocumentoHandler, setAddDocumentoHandler] = useState(null);
	// Referência para função de adicionar produto da AbaProdutos
	const [addProdutoHandler, setAddProdutoHandler] = useState(null);

	// Pegar unidade distribuidora do localStorage
	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	useEffect(() => {
		if (id) {
			loadFornecedor(id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	// Ao abrir o cadastro vindo de uma solicitação, busca a propriedade de origem
	// pelo id salvo na solicitação (propriedadeOrigemId) para vincular, preencher
	// o número sequencial/endereço e trazer os cultivos como produtos.
	useEffect(() => {
		if (id || !location.state?.fromSolicitacao) return;
		const propriedadeOrigemId = location.state?.propriedadeOrigemId;
		if (!propriedadeOrigemId) return;

		(async () => {
			try {
				const fornecedorService = new FornecedorService();
				const propriedade = await fornecedorService.buscarPorPropriedadeId(propriedadeOrigemId);
				if (!propriedade) return;

				setDadosEmpresa((prev) => ({
					...prev,
					nome: prev.nome || propriedade.nome || "",
					propriedadeOrigemId: propriedade.id,
					numeroSequencialPropriedade: propriedade.numeroSequencial,
					cep: prev.cep || propriedade.endereco?.cep || "",
					uf: prev.uf || propriedade.endereco?.uf || "",
					cidade: prev.cidade || propriedade.endereco?.cidade || "",
					bairro: prev.bairro || propriedade.endereco?.bairro || "",
					logradouro: prev.logradouro || propriedade.endereco?.endereco || "",
				}));

				const cultivos = propriedade.propriedadeCultivos || [];
				if (cultivos.length > 0) {
					setProdutos(
						cultivos.map((pc) => ({
							cultivoId: pc.cultivoId,
							unidadeMedidaId: pc.unidadeMedidaId || null,
							cultivo: pc.cultivo,
							unidadeMedida: pc.unidadeMedida || null,
						}))
					);
				}
			} catch (error) {
				console.error("Erro ao vincular propriedade da solicitação:", error);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const formatCpfCnpj = (value) => {
		if (!value) return "";
		const cleanValue = value.replace(/\D/g, "");

		if (cleanValue.length <= 11) {
			return cleanValue
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
		} else {
			return cleanValue
				.replace(/(\d{2})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1/$2")
				.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
		}
	};

	const loadFornecedor = async (fornecedorId) => {
		setLoading(true);
		try {
			const fornecedorService = new FornecedorService();
			const data = await fornecedorService.getById(fornecedorId);

			// Carregar dados da empresa
			setDadosEmpresa({
				id: data.id,
				nome: data.nome || "",
				nomeFantasia: data.nomeFantasia || "",
				cpfCnpj: formatCpfCnpj(data.cpfCnpj || ""),
				ieRg: data.ieRg || "",
				email: data.email || "",
				telefone: data.telefone || "",
				propriedadeOrigemId: data.propriedadeOrigemId || "",
				numeroSequencialPropriedade: data.numeroSequencialPropriedade || "",
				dataEntradaBase: data.dataEntradaBase ? data.dataEntradaBase.split('T')[0] : "",
				previsaoPagamentoDias: data.previsaoPagamentoDias || "",
				cep: data.endereco?.cep || "",
				uf: data.endereco?.uf || "",
				cidade: data.endereco?.cidade || "",
				bairro: data.endereco?.bairro || "",
				logradouro: data.endereco?.logradouro || "",
				latitude: data.endereco?.latitude || "",
				longitude: data.endereco?.longitude || "",
				documentos: (data.documentos || []).map(doc => ({
					id: doc.id,
					nome: doc.nome,
					dataExpedicao: doc.dataExpedicao,
					dataValidade: doc.dataValidade,
					arquivo: doc.arquivoUrl, // Mapear arquivoUrl para arquivo
					arquivoKey: doc.arquivoKey, // Também salvar a key para preservar
				})),
			});

			// Carregar produtos
			const produtosFormatados = (data.produtos || []).map((produto) => ({
				id: produto.id,
				cultivo: produto.cultivo,
				unidadeMedida: produto.unidadeMedida,
				cultivoId: produto.cultivoId,
				unidadeMedidaId: produto.unidadeMedidaId,
			}));
			setProdutos(produtosFormatados);
		} catch (error) {
			console.error("Erro ao carregar fornecedor:", error);
			alert(error.message || "Erro ao carregar dados do fornecedor");
		} finally {
			setLoading(false);
		}
	};

	const handleSalvar = async () => {
		try {
			// Validações básicas
			if (!dadosEmpresa.nome || !dadosEmpresa.cpfCnpj) {
				alert("Preencha os campos obrigatórios: Nome e CPF/CNPJ");
				setAbaAtiva("empresa");
				return;
			}

			setSalvando(true);

			// Criar FormData para enviar arquivos
			const formData = new FormData();

			// Preparar dados para envio
			const fornecedorData = {
				id: dadosEmpresa.id || undefined,
				unidadeDistribuidoraId: unidadeDistribuidoraId,
				nome: dadosEmpresa.nome,
				nomeFantasia: dadosEmpresa.nomeFantasia,
				cpfCnpj: dadosEmpresa.cpfCnpj ? dadosEmpresa.cpfCnpj.replace(/\D/g, "") : "",
				ieRg: dadosEmpresa.ieRg,
				email: dadosEmpresa.email,
				telefone: dadosEmpresa.telefone,
				propriedadeOrigemId: dadosEmpresa.propriedadeOrigemId || undefined,
				solicitarClienteCampo,
				numeroSequencialPropriedade: dadosEmpresa.numeroSequencialPropriedade || undefined,
				dataEntradaBase: dadosEmpresa.dataEntradaBase || undefined,
				previsaoPagamentoDias: dadosEmpresa.previsaoPagamentoDias ? parseInt(dadosEmpresa.previsaoPagamentoDias) : undefined,
				endereco: {
					cep: dadosEmpresa.cep,
					uf: dadosEmpresa.uf,
					cidade: dadosEmpresa.cidade,
					bairro: dadosEmpresa.bairro,
					logradouro: dadosEmpresa.logradouro,
					latitude: dadosEmpresa.latitude ? parseFloat(dadosEmpresa.latitude) : undefined,
					longitude: dadosEmpresa.longitude ? parseFloat(dadosEmpresa.longitude) : undefined,
				},
				documentos: [],
				produtos: [],
			};

			// Processar documentos
			if (dadosEmpresa.documentos && dadosEmpresa.documentos.length > 0) {
				let fileIndex = 0;
				dadosEmpresa.documentos.forEach((doc) => {
					const docData = {
						nome: doc.nome,
						dataExpedicao: doc.dataExpedicao,
						dataValidade: doc.dataValidade,
					};

					// Se é um arquivo novo (File), adicionar ao formData e marcar o índice
					if (doc.arquivo && doc.arquivo instanceof File) {
						docData.indexArquivo = fileIndex;
						formData.append('files', doc.arquivo);
						fileIndex++;
					}
					// Se é uma URL (arquivo existente), preservar a URL e key
					else if (doc.arquivo && typeof doc.arquivo === 'string') {
						docData.arquivoUrl = doc.arquivo;
						docData.arquivoKey = doc.arquivoKey; // Preservar a key original
					}

					fornecedorData.documentos.push(docData);
				});
			}

			// Processar produtos
			if (produtos && produtos.length > 0) {
				fornecedorData.produtos = produtos.map((produto) => ({
					cultivoId: produto.cultivoId,
					unidadeMedidaId: produto.unidadeMedidaId,
				}));
			}

			// Adicionar dados JSON ao FormData
			formData.append("data", JSON.stringify(fornecedorData));

			// Salvar no backend
			const fornecedorService = new FornecedorService();

			if (id) {
				// Atualizar
				await fornecedorService.update(formData);
				alert("Fornecedor atualizado com sucesso!");
			} else {
				// Criar
				await fornecedorService.save(formData);

				// Só agora, com o fornecedor salvo, marca a solicitação como aceita.
				if (location.state?.fromSolicitacao && location.state?.solicitacaoId) {
					try {
						await fornecedorService.aceitarSolicitacao(location.state.solicitacaoId);
					} catch (e) {
						console.error("Fornecedor salvo, mas falha ao marcar solicitação como aceita:", e);
					}
				}

				alert("Fornecedor cadastrado com sucesso!");
			}

			navigate("/fornecedores");
		} catch (error) {
			console.error("Erro ao salvar fornecedor:", error);
			alert(error.message || "Erro ao salvar fornecedor");
		} finally {
			setSalvando(false);
		}
	};

	const handleAddDocumentos = () => {
		if (abaAtiva === "empresa" && addDocumentoHandler) {
			addDocumentoHandler();
		}
	};

	const handleAddProdutos = () => {
		if (abaAtiva === "produtos" && addProdutoHandler) {
			addProdutoHandler();
		}
	};

	const renderAbaContent = () => {
		switch (abaAtiva) {
			case "empresa":
				return (
					<AbaEmpresa
						dados={dadosEmpresa}
						setDados={setDadosEmpresa}
						onAddDocumentoClick={setAddDocumentoHandler}
					/>
				);
			case "produtos":
				return (
					<AbaProdutos
						produtos={produtos}
						setProdutos={setProdutos}
						onAddProdutoClick={setAddProdutoHandler}
					/>
				);
			default:
				return null;
		}
	};

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "100vh",
				}}
			>
				<Typography>Carregando...</Typography>
			</Box>
		);
	}

	return (
		<AppLayout currentPage="fornecedores">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Header com título */}
					<Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
						<IconButton onClick={() => navigate("/fornecedores")} sx={{ color: "#666" }}>
							<ArrowBackIcon />
						</IconButton>
						<Typography
							variant="h5"
							sx={{
								color: "#666",
								fontWeight: 500,
								fontSize: "20px",
							}}
						>
							Cadastro de Fornecedor
						</Typography>
					</Box>

					{/* Container com fundo branco */}
					<Box
						sx={{
							bgcolor: "white",
							borderRadius: "4px",
							boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
							overflow: "hidden",
						}}
					>
						{/* Header com abas e botão */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								px: 6,
								pt: 4,
								pb: 0,
							}}
						>
							{/* Navegação das abas */}
							<Box sx={{ display: "flex", gap: 0.5 }}>
								<Button
									onClick={() => setAbaAtiva("empresa")}
									sx={{
										textTransform: "none",
										fontSize: "15px",
										fontWeight: 500,
										bgcolor: abaAtiva === "empresa" ? "#1a9e8f" : "transparent",
										color: abaAtiva === "empresa" ? "white" : "#1a9e8f",
										borderRadius: "6px 6px 0 0",
										px: 3,
										py: 1.5,
										minWidth: "auto",
										minHeight: "42px",
										"&:hover": {
											bgcolor: abaAtiva === "empresa" ? "#138a7d" : "rgba(23, 162, 184, 0.08)",
										},
									}}
								>
									Empresa
								</Button>
								<Button
									onClick={() => setAbaAtiva("produtos")}
									sx={{
										textTransform: "none",
										fontSize: "15px",
										fontWeight: 500,
										bgcolor: abaAtiva === "produtos" ? "#1a9e8f" : "transparent",
										color: abaAtiva === "produtos" ? "white" : "#1a9e8f",
										borderRadius: "6px 6px 0 0",
										px: 3,
										py: 1.5,
										minWidth: "auto",
										minHeight: "42px",
										"&:hover": {
											bgcolor: abaAtiva === "produtos" ? "#138a7d" : "rgba(23, 162, 184, 0.08)",
										},
									}}
								>
									Produtos
								</Button>
							</Box>

							{/* Botão Adicionar documentos/produtos */}
							{(abaAtiva === "empresa" || abaAtiva === "produtos") && (
								<Button
									variant="contained"
									onClick={
										abaAtiva === "empresa"
											? handleAddDocumentos
											: handleAddProdutos
									}
									sx={{
										bgcolor: "#1a9e8f",
										"&:hover": {
											bgcolor: "#138a7d",
											boxShadow: "0 2px 4px rgba(23, 162, 184, 0.3)",
										},
										textTransform: "none",
										px: 3,
										py: 1.25,
										fontSize: "15px",
										fontWeight: 500,
										borderRadius: "4px",
										boxShadow: "none",
									}}
								>
									{abaAtiva === "empresa"
										? "Adicionar documentos"
										: "Adicionar produto"}
								</Button>
							)}
						</Box>

						{/* Conteúdo da aba */}
						<Box sx={{ px: 6, py: 4, minHeight: "400px" }}>{renderAbaContent()}</Box>

						{/* Fornecedor vinculado a uma propriedade do campo: opção de gerar
							solicitação para o campo cadastrar a unidade como cliente */}
						{!id && dadosEmpresa.propriedadeOrigemId && (
							<Box sx={{ px: 6, pb: 2 }}>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 1,
										p: "12px 16px",
										backgroundColor: "#fff3e0",
										border: "1px solid #ffb74d",
										borderLeft: "4px solid #f57c00",
										borderRadius: "4px",
										textAlign: "left",
									}}
								>
									<Typography variant="subtitle2" sx={{ color: "#5f3000", fontWeight: 700 }}>
										Conectar com o produtor?
									</Typography>
									<Typography variant="body2" sx={{ color: "#5f3000" }}>
										Você cadastrou um fornecedor que também utiliza a Plataforma Sisagri. Deseja solicitar uma conexão com este produtor?
									</Typography>
									<Typography variant="body2" sx={{ color: "#5f3000" }}>
										Após a aprovação do produtor, você poderá receber automaticamente as informações das vendas realizadas por ele, reduzindo retrabalho e evitando lançamentos manuais.
									</Typography>
									<Typography variant="caption" sx={{ color: "#5f3000" }}>
										O compartilhamento ocorrerá apenas após a aprovação da solicitação por ambas as partes.
									</Typography>
									<FormControlLabel
										control={
											<Checkbox
												checked={solicitarClienteCampo}
												onChange={(e) => setSolicitarClienteCampo(e.target.checked)}
												color="warning"
												size="small"
												sx={{ py: 0 }}
											/>
										}
										label={
											<Typography variant="body2" sx={{ color: "#5f3000", fontWeight: 600 }}>
												Sim, solicitar conexão com este produtor
											</Typography>
										}
										sx={{ m: 0, alignItems: "center" }}
									/>
								</Box>
							</Box>
						)}

						{/* Botão Cadastrar no canto inferior direito */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								px: 6,
								pb: 4,
								pt: 0,
							}}
						>
							<Button
								variant="contained"
								onClick={handleSalvar}
								disabled={salvando}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
										boxShadow: "0 2px 6px rgba(23, 162, 184, 0.4)",
									},
									"&:disabled": {
										bgcolor: "#ccc",
									},
									textTransform: "none",
									px: 5,
									py: 1.5,
									fontSize: "15px",
									fontWeight: 500,
									borderRadius: "4px",
									boxShadow: "none",
								}}
							>
								{salvando ? "Salvando..." : id ? "Atualizar" : "Cadastrar"}
							</Button>
						</Box>
					</Box>
			</Box>
		</AppLayout>
	);
}

export default FornecedoresCad;
